import React, { createContext, forwardRef, useContext } from 'react'
import type { CSSObject } from '@emotion/react'

import {
  KitElement,
  TextAncestorContext,
  attachPlatformMethods,
  coloring,
  composeGroups,
  flexItem,
  layout,
  resolveStyleProps,
  roleBaseStyle,
  sizing,
  spacing,
  toDOMProps,
  typography,
  useLayoutHandler,
  useMergedRef,
  type KitViewProps,
  type RNStyle
} from '../_support'
import type { ThemeColor, ThemeFont } from '@theme'

/* -----------------------------------------------------------------------------
 * Text
 *
 * react-native's Text on the web: a block `div` at the root and an inline
 * `span` inside another Text, which inherits its font and color. `variant`
 * sets a font and color pair (body by default); TextBlock passes default props
 * to the Text inside it.
 * -------------------------------------------------------------------------- */

export const textStyles = composeGroups(
  flexItem,
  layout,
  spacing,
  sizing,
  coloring,
  typography
)

type Variant =
  | 'fine'
  | 'badge'
  | 'meta'
  | 'label'
  | 'small'
  | 'subtitle'
  | 'body'
  | 'title'
  | 'h3'
  | 'h2'
  | 'h1'

export const variants: Record<Variant, TextProps> = {
  fine: { textColor: 'shade05', font: 'text.10.medium' },
  badge: {
    textColor: 'purple',
    textTransform: 'uppercase',
    font: 'text.10.bold'
  },
  meta: { textColor: 'shade05', font: 'text.12.medium' },
  label: {
    textColor: 'purple',
    textTransform: 'uppercase',
    font: 'text.12.bold'
  },
  small: { textColor: 'shade06', font: 'text.14.medium' },
  subtitle: { textColor: 'purple', font: 'text.14.bold' },
  body: { textColor: 'shade07', font: 'text.16.medium' },
  title: { textColor: 'purple', font: 'text.16.bold' },
  h3: { textColor: 'purple', font: 'display.20.bold' },
  h2: { textColor: 'purple', font: 'display.24.bold' },
  h1: { textColor: 'purple', font: 'display.32.bold' }
}

export type TextProps = KitViewProps & {
  variant?: Variant
  truncate?: boolean
  numberOfLines?: number
  ellipsizeMode?: string
  selectable?: boolean
  onPress?: (event: React.MouseEvent) => void
  textColor?: ThemeColor | (string & {})
  font?: ThemeFont
}

export const TextBlockContext = createContext<TextProps>({})

export const TextBlock = ({ children, ...props }: TextProps) => (
  <TextBlockContext.Provider value={props}>
    {children}
  </TextBlockContext.Provider>
)

// react-native-web's Text resets
const TEXT_BASE: RNStyle = {
  borderWidth: 0,
  borderStyle: 'solid',
  borderColor: 'black',
  boxSizing: 'border-box',
  color: 'black',
  display: 'inline',
  font: '14px System',
  margin: 0,
  padding: 0,
  whiteSpace: 'pre-wrap',
  wordWrap: 'break-word'
}
const TEXT_NESTED: RNStyle = {
  color: 'inherit',
  font: 'inherit',
  whiteSpace: 'inherit'
}
const ONE_LINE: RNStyle = {
  maxWidth: '100%',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  wordWrap: 'normal'
}
const MULTI_LINE: RNStyle = {
  display: '-webkit-box',
  maxWidth: '100%',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  WebkitBoxOrient: 'vertical'
}

const Text = forwardRef<HTMLElement, TextProps>((props, ref) => {
  const {
    truncate,
    variant = 'body',
    ...rest
  } = {
    ...useContext(TextBlockContext),
    ...props
  }
  const truncation = truncate ? { numberOfLines: 1, ellipsizeMode: 'tail' } : {}
  const {
    numberOfLines,
    ellipsizeMode,
    selectable,
    onPress,
    onLayout,
    sx,
    ...textProps
  } = { ...variants[variant], ...truncation, ...rest } as TextProps

  const hasTextAncestor = useContext(TextAncestorContext)
  const layoutRef = useLayoutHandler(onLayout)
  const mergedRef = useMergedRef(ref, layoutRef, attachPlatformMethods)

  const base: RNStyle = {
    ...TEXT_BASE,
    ...(hasTextAncestor ? TEXT_NESTED : {}),
    ...(numberOfLines === 1 ? ONE_LINE : {}),
    ...(numberOfLines != null && numberOfLines > 1 ? MULTI_LINE : {}),
    ...(selectable === false ? { userSelect: 'none' } : {}),
    ...(selectable === true ? { userSelect: 'text' } : {}),
    ...(onPress ? { cursor: 'pointer' } : {}),
    ...roleBaseStyle(textProps, hasTextAncestor ? 'span' : 'div')
  }
  const { css, rest: forwarded } = resolveStyleProps(textProps, textStyles, {
    base,
    inline:
      numberOfLines != null && numberOfLines > 1
        ? { WebkitLineClamp: numberOfLines }
        : {}
  })
  const { domProps, element, pointerStyle, pointerChildren } =
    toDOMProps(forwarded)
  if (!hasTextAncestor) domProps.dir = domProps.dir ?? 'auto'
  if (onPress) domProps.onClick = onPress

  const node = (
    <KitElement
      as={(element || (hasTextAncestor ? 'span' : 'div')) as any}
      ref={mergedRef}
      kitCss={{ ...css, ...pointerStyle, ...pointerChildren } as CSSObject}
      sx={sx}
      {...domProps}
    />
  )

  return hasTextAncestor ? (
    node
  ) : (
    <TextAncestorContext.Provider value={true}>
      {node}
    </TextAncestorContext.Provider>
  )
})
Text.displayName = 'Text'

export default Text
